// pages/sections/StaleJobSection.js
// Stale job review with JM → JP two-step workflow. Managers see jobs where
// JMEmail matches them; partners see jobs where JPEmail matches AND the manager
// has already marked the row reviewed.
// Actions per row: three mutually-independent toggles —
//   * close and roll      (writeback to PE via stub)
//   * close and no roll   (writeback to PE via stub)
//   * mark reviewed       (audit log only)
// Close actions are mutually exclusive: turning one on clears the other.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { usePeriod } from '../../hooks/usePeriod';
import { useSectionData } from '../../hooks/useSectionData';
import { useRowMutations } from '../../hooks/useRowMutations';
import LastRefreshed from '../../components/LastRefreshed';
import ToggleSwitch from './ToggleSwitch';
import RoleToggle from './RoleToggle';
import { postJson, postAdminUnmark, patchRowIn, sortDoneLast } from './reviewUtil';
import './ReviewTable.css';

const PAGE_SIZE = 25;

export default function StaleJobSection() {
  const { email, isAdmin } = useAuth();
  const { period, periodActive } = usePeriod();

  // Role selection is client-side only — backend returns jobs where the
  // caller is either JM or JP. Switching between manager and partner filters
  // the visible slice without a refetch.
  const [activeRole, setActiveRole] = useState('manager');

  const [search, setSearch]   = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'pending' | 'reviewed'
  const [page, setPage]       = useState(1);

  const fetcher = useCallback(async () => {
    if (!period) return null;
    const res = await fetch(`/api/diStaleJobs/${period}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [period]);

  // Optimistic saves: ordered per job, never disable the controls, and a refetch can't overwrite an edit.
  const mutations = useRowMutations(r => r.jobIdx);
  const { pinned, clearPinned } = mutations;

  const { data, setData, initialLoading, error, lastRefreshedAt, freshness, silentRefetch }
    = useSectionData(fetcher, {
      deps: [period, email],
      heartbeat: { sections: ['stale-job-review'], period },
      reconcile: mutations.reconcile,
    });

  // Rows edited this session stay listed (e.g. under "Pending") until the user changes a filter.
  useEffect(() => { clearPinned(); }, [search, officeFilter, statusFilter, activeRole, clearPinned]);

  const fireAction = (jobIdx, action, role) =>
    postJson(`/api/diStaleJobs/${period}`, { action, jobIdx, role });

  // Optimistic row patcher over setData. (Snapshots for revert come from the RENDERED row,
  // never from inside the updater — React may run the updater later.)
  const patchRow = (jobIdx, updater) => patchRowIn(setData, jobIdx, updater);
  const renderedRow = jobIdx => (data?.rows || []).find(r => String(r.jobIdx) === String(jobIdx));

  // Translate a postAction action + role into an optimistic row patch.
  function applyActionLocally(row, action, role) {
    if (action === 'mark-reviewed') {
      return {
        ...row,
        actions: {
          ...row.actions,
          [role]: { ...(row.actions?.[role] || {}), reviewed: true, reviewedAt: new Date().toISOString() },
        },
      };
    }
    if (action === 'close-and-roll' || action === 'close-no-roll') {
      return {
        ...row,
        actions: {
          ...row.actions,
          [role]: { ...(row.actions?.[role] || {}), closeAction: action },
        },
      };
    }
    if (action === 'clear') {
      return {
        ...row,
        actions: {
          ...row.actions,
          [role]: { ...(row.actions?.[role] || {}), closeAction: null },
        },
      };
    }
    return row;
  }

  async function postAction(jobIdx, action) {
    // The rendered row = its state before this edit (a setData updater may run later).
    // Cascade precheck for mark-reviewed (before any patch, so we compare against real state).
    const snapshot    = renderedRow(jobIdx);
    const role        = activeRole;
    const otherRole   = role === 'manager' ? 'partner' : 'manager';
    const myEmail     = (email || '').toLowerCase();
    const jm          = (snapshot?.jmEmail || '').toLowerCase();
    const jp          = (snapshot?.jpEmail || '').toLowerCase();
    const isBothRoles = jm === myEmail && jp === myEmail && !!myEmail;
    const otherAlreadyReviewed = !!snapshot?.actions?.[otherRole]?.reviewed;
    const cascadeNeeded = action === 'mark-reviewed' && isBothRoles && !otherAlreadyReviewed;

    // Optimistic patch.
    patchRow(jobIdx, r => {
      const next = applyActionLocally(r, action, role);
      return cascadeNeeded ? applyActionLocally(next, 'mark-reviewed', otherRole) : next;
    });

    let cascadeError = null;
    try {
      // Queued per job so rapid clicks reach the server in order; the controls stay live meanwhile.
      await mutations.run(jobIdx, async () => {
        await fireAction(jobIdx, action, role);
        if (cascadeNeeded) {
          try {
            await fireAction(jobIdx, 'mark-reviewed', otherRole);
          } catch (err) {
            cascadeError = err; // the primary save stands; only the cascade half is undone below
          }
        }
      }, {
        revert: () => { if (snapshot) patchRow(jobIdx, () => snapshot); },
        refetch: silentRefetch,
      });
    } catch (err) {
      toast.error(`Failed: ${err.message}`, { autoClose: 10000 });
      return;
    }

    if (cascadeError) {
      // Primary succeeded; only revert the cascade portion.
      patchRow(jobIdx, r => ({
        ...r,
        actions: { ...r.actions, [otherRole]: snapshot?.actions?.[otherRole] || { ...(r.actions?.[otherRole] || {}), reviewed: false, reviewedAt: null } },
      }));
      toast.warning(`Primary review saved; cascade to ${otherRole} failed: ${cascadeError.message}`, { autoClose: 10000 });
    } else if (cascadeNeeded) {
      toast.success('Reviewed (manager + partner)');
    } else {
      toast.success(`Saved: ${action.replace(/-/g, ' ')}`);
    }
  }

  const fireAdminUnmark = (jobIdx, role) => postAdminUnmark(period, 'stale-job-review', jobIdx, role);

  async function adminUnmark(jobIdx) {
    if (!isAdmin) return;
    const row = renderedRow(jobIdx);
    const label = row ? `${row.clientCode} — ${row.jobName}` : 'this job';
    // Cascade-unmark when JM and JP are the same user — keeps both signoffs
    // in sync, mirroring the cascade on the mark-reviewed side.
    const jm = (row?.jmEmail || '').toLowerCase();
    const jp = (row?.jpEmail || '').toLowerCase();
    const cascade = !!jm && jm === jp;
    const role = activeRole;

    const confirmMsg = cascade
      ? `Unmark ${label} as reviewed? JM and JP are the same user, so both signoffs will be cleared.`
      : `Unmark ${label} as reviewed for ${role}?`;
    if (!window.confirm(confirmMsg)) return;

    const snapshot = row;
    // Unmarking the manager also clears the partner's sign-off server-side (it rests on the manager's).
    const clearsBoth = cascade || role === 'manager';
    patchRow(jobIdx, r => ({
      ...r,
      actions: clearsBoth
        ? {
            ...r.actions,
            manager: { ...(r.actions?.manager || {}), reviewed: false, reviewedAt: null },
            partner: { ...(r.actions?.partner || {}), reviewed: false, reviewedAt: null },
          }
        : {
            ...r.actions,
            [role]: { ...(r.actions?.[role] || {}), reviewed: false, reviewedAt: null },
          },
    }));
    try {
      await mutations.run(jobIdx, async () => {
        if (cascade) {
          await fireAdminUnmark(jobIdx, 'manager');
          await fireAdminUnmark(jobIdx, 'partner');
        } else {
          await fireAdminUnmark(jobIdx, role);
        }
      }, {
        revert: () => { if (snapshot) patchRow(jobIdx, () => snapshot); },
        refetch: silentRefetch,
      });
      toast.success(cascade ? 'Unmarked reviewed (manager + partner).' : 'Unmarked reviewed.');
    } catch (err) {
      toast.error(`Failed: ${err.message}`, { autoClose: 10000 });
    }
  }

  // Partner claims a job whose manager hasn't reviewed yet — writes a
  // manager signoff first (audit_log user_id = partner, role = 'manager' so
  // the attribution is visible), then the partner signoff.
  async function pullToSelf(jobIdx) {
    const snapshot = renderedRow(jobIdx);
    const now = new Date().toISOString();
    patchRow(jobIdx, r => ({
      ...r,
      actions: {
        ...r.actions,
        manager: { ...(r.actions?.manager || {}), reviewed: true, reviewedAt: now },
        partner: { ...(r.actions?.partner || {}), reviewed: true, reviewedAt: now },
      },
    }));
    let partnerError = null;
    try {
      await mutations.run(jobIdx, async () => {
        await fireAction(jobIdx, 'mark-reviewed', 'manager');
        try {
          await fireAction(jobIdx, 'mark-reviewed', 'partner');
        } catch (err) {
          partnerError = err; // the manager half is already recorded server-side
        }
      }, {
        revert: () => { if (snapshot) patchRow(jobIdx, () => snapshot); },
        refetch: silentRefetch,
      });
    } catch (err) {
      toast.error(`Pull failed: ${err.message}`, { autoClose: 10000 });
      return;
    }
    if (partnerError) {
      // Manager signoff landed, partner's did not: show the real state.
      patchRow(jobIdx, r => ({
        ...r,
        actions: { ...r.actions, partner: snapshot?.actions?.partner || { ...(r.actions?.partner || {}), reviewed: false, reviewedAt: null } },
      }));
      toast.warning(`Manager sign-off saved; partner sign-off failed: ${partnerError.message}`, { autoClose: 10000 });
    } else {
      toast.success('Pulled to partner (manager + partner both marked).');
    }
  }

  const offices = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.rows.map(r => r.jobOffice))].filter(Boolean).sort();
  }, [data]);

  // Role-scoped rows. A user sees a job in the manager view only if they're
  // the assigned JM; in the partner view only if they're the assigned JP.
  // Applies to everyone including admins — firm-wide visibility lives in
  // Steering, not on the section page.
  const roleRows = useMemo(() => {
    if (!data) return [];
    const me = (email || '').toLowerCase();
    const emailField = activeRole === 'manager' ? 'jmEmail' : 'jpEmail';
    return data.rows.filter(r => (r[emailField] || '').toLowerCase() === me);
  }, [data, email, activeRole]);

  const filtered = useMemo(() => {
    let out = roleRows;
    if (search) {
      const s = search.toLowerCase();
      out = out.filter(r =>
        r.clientCode.toLowerCase().includes(s) ||
        r.clientName.toLowerCase().includes(s) ||
        r.jobName.toLowerCase().includes(s)
      );
    }
    if (officeFilter) out = out.filter(r => r.jobOffice === officeFilter);
    // Rows the user just edited stay put even if that moved them out of this status.
    const keep = r => pinned.has(String(r.jobIdx));
    if (statusFilter === 'pending')  out = out.filter(r => keep(r) || !r.actions[activeRole]?.reviewed);
    if (statusFilter === 'reviewed') out = out.filter(r => keep(r) ||  r.actions[activeRole]?.reviewed);
    return sortDoneLast(out, r => !!r.actions[activeRole]?.reviewed);
  }, [roleRows, search, officeFilter, statusFilter, activeRole, pinned]);

  // Recompute summary from role-scoped rows (rather than using the server
  // payload's summary which is no longer role-aware). Must be declared BEFORE
  // any early returns so hook count stays stable across renders.
  const summary = useMemo(() => {
    const totalJobs     = roleRows.length;
    const reviewedCount = roleRows.filter(r => r.actions?.[activeRole]?.reviewed).length;
    return { totalJobs, reviewedCount, pendingCount: totalJobs - reviewedCount };
  }, [roleRows, activeRole]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (initialLoading) return <div className="rt-loading">Loading stale jobs…</div>;
  if (error && !data) return <div className="rt-error">Error: {error}</div>;
  if (!data)   return null;

  const readOnly = !periodActive;

  return (
    <div className="rt-page">
      <div className="rt-header">
        <div className="rt-title-block">
          <h2 className="rt-title">Stale Job Review</h2>
          {readOnly && <span className="rt-readonly-badge">Read-only</span>}
          <LastRefreshed at={lastRefreshedAt} freshness={freshness} onRefresh={silentRefetch} />
        </div>
        <RoleToggle role={activeRole} onChange={setActiveRole} disabled={initialLoading} />
      </div>

      {(() => {
        const totalNow    = summary.totalJobs;
        const reviewedNow = summary.reviewedCount;
        const pct = totalNow > 0 ? Math.round((reviewedNow / totalNow) * 100) : 0;
        return (
          <div className="rt-progress-section">
            <div className="rt-progress-header">
              <h3 className="rt-progress-title">Your Progress</h3>
              <span className="rt-progress-label-val">
                <span className="rt-progress-count">{reviewedNow} / {totalNow}</span>
                <span className="rt-progress-divider">—</span>
                {pct}% Complete
              </span>
            </div>
            <div className="rt-progress-track">
              <div
                className="rt-progress-fill"
                style={{
                  width: `${Math.min(100, Math.max(0, pct))}%`,
                  background: pct >= 100 ? '#1a7a4a' : pct >= 50 ? '#e6a817' : '#c0392b',
                }}
              />
            </div>
          </div>
        );
      })()}

      <div className="rt-summary">
        <div className="rt-stat"><span className="rt-stat-val">{summary.totalJobs}</span><span className="rt-stat-lbl">Your Jobs</span></div>
        <div className="rt-stat rt-stat--pending"><span className="rt-stat-val">{summary.pendingCount}</span><span className="rt-stat-lbl">Needs Review</span></div>
        <div className="rt-stat rt-stat--done"><span className="rt-stat-val">{summary.reviewedCount}</span><span className="rt-stat-lbl">Reviewed</span></div>
      </div>

      {activeRole === 'partner' && summary.totalJobs === 0 && (
        <div className="rt-empty" style={{ background: '#fff3de', borderRadius: 8, color: '#7a5a1a', padding: 20 }}>
          No stale jobs are currently assigned to you.
        </div>
      )}

      <div className="rt-filters">
        <input className="rt-filter-input" placeholder="Search by client code, client name, or job…"
               value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select className="rt-filter-select" value={officeFilter}
                onChange={e => { setOfficeFilter(e.target.value); setPage(1); }}>
          <option value="">All Offices</option>
          {offices.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select className="rt-filter-select" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
        </select>
      </div>

      {activeRole === 'partner' && roleRows.some(r => !r.actions?.manager?.reviewed) && (
        <div className="rt-partner-note">
          <i className="fas fa-info-circle" />
          <span>
            If your manager hasn't reviewed a job yet, you can still mark it reviewed —
            your action will sign off as both Manager and Partner.
          </span>
        </div>
      )}

      <div className="rt-table-wrap">
        <table className="rt-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Job</th>
              <th>Office</th>
              <th>{activeRole === 'manager' ? 'Job Partner' : 'Job Manager'}</th>
              <th>Status</th>
              <th>Current Staff</th>
              <th>Orig Due</th>
              <th>Current Due</th>
              <th>Close & Roll</th>
              <th>Close & No Roll</th>
              <th>Mark Reviewed</th>
            </tr>
          </thead>
          <tbody>
            {paged.map(row => {
              const myActions = row.actions[activeRole];
              const reviewed = myActions.reviewed;
              const closeAction = myActions.closeAction;
              // Partner view: if the manager hasn't reviewed yet, the row is
              // still "pending manager" — we show it so the partner sees the
              // full pipeline, but their action toggles stay disabled.
              const pendingManager = activeRole === 'partner' && !row.actions?.manager?.reviewed;
              const partnerSigned  = activeRole === 'manager' && !!row.actions?.partner?.reviewed;
              const lockForPending = pendingManager;

              return (
                <tr key={row.jobIdx}
                    className={`${reviewed ? 'rt-row-reviewed' : ''} ${pendingManager ? 'rt-row-pending-mgr' : ''}`}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{row.clientCode}</div>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>{row.clientName}</div>
                  </td>
                  <td>{row.jobName}</td>
                  <td>{row.jobOffice}</td>
                  <td>
                    {(activeRole === 'manager' ? row.jobPartner : row.jobManager)
                      || <span className="rt-cell-muted">—</span>}
                  </td>
                  <td>{row.jobWorkStatus}</td>
                  <td>{row.currentStaff || <span className="rt-cell-muted">—</span>}</td>
                  <td>{row.originalDueDate}</td>
                  <td>{row.currentDueDate}</td>
                  <td>
                    <ToggleSwitch
                      variant="warning"
                      checked={closeAction === 'close-and-roll'}
                      disabled={readOnly || reviewed || lockForPending}
                      onChange={checked => {
                        if (checked) postAction(row.jobIdx, 'close-and-roll');
                        else         postAction(row.jobIdx, 'clear');
                      }}
                    />
                  </td>
                  <td>
                    <ToggleSwitch
                      variant="danger"
                      checked={closeAction === 'close-no-roll'}
                      disabled={readOnly || reviewed || lockForPending}
                      onChange={checked => {
                        if (checked) postAction(row.jobIdx, 'close-no-roll');
                        else         postAction(row.jobIdx, 'clear');
                      }}
                    />
                  </td>
                  <td className="rt-mark-reviewed-cell" onClick={e => e.stopPropagation()}>
                    {pendingManager && !reviewed && (
                      <span className="rt-pending-mgr-corner"
                            title="Manager hasn't reviewed this job yet — marking here will pull the review to you.">
                        Pending Manager
                      </span>
                    )}
                    {partnerSigned && (
                      <span className="rt-partner-signed-corner"
                            title="Partner has already signed off on this job.">
                        Partner Signed Off
                      </span>
                    )}
                    <ToggleSwitch
                      checked={reviewed}
                      disabled={readOnly || (!isAdmin && (reviewed || partnerSigned))}
                      onChange={checked => {
                        if (checked && !reviewed) {
                          if (pendingManager) pullToSelf(row.jobIdx);
                          else                postAction(row.jobIdx, 'mark-reviewed');
                        } else if (!checked && reviewed && isAdmin) {
                          adminUnmark(row.jobIdx);
                        }
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && summary.totalJobs > 0 && (
          <div className="rt-empty">No jobs match.</div>
        )}
      </div>

      {filtered.length > PAGE_SIZE && (
        <div className="rt-pagination">
          <button disabled={safePage <= 1} onClick={() => setPage(1)}>«</button>
          <button disabled={safePage <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
          <span className="rt-page-info">Page {safePage} of {totalPages} ({filtered.length} rows)</span>
          <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}>»</button>
        </div>
      )}
    </div>
  );
}
